import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, Subscription, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { UserService } from '../users.service';
import { AppUser, BranchSummary, BrandSummary, BusinessSummary, BusinessUnitSummary, CustomerSummary, SupplierSummary } from '../user.model';
import { BranchesService } from '../../master-data/branches/branches.service';
import { BrandsService } from '../../master-data/brands/brands.service';
import { BusinessUnitsService } from '../../master-data/business-units/business-units.service';
import { BusinessService } from '../../master-data/business/business.service';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { CustomersService } from '../../master-data/customers/customers.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { MessageService } from 'primeng/api';

// PrimeNG Modules
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { AvatarModule } from 'primeng/avatar';
import { TableModule } from "primeng/table";
import { BadgeModule } from "primeng/badge";
import { AccordionModule } from "primeng/accordion";
import { SkeletonModule } from "primeng/skeleton";
import { ToastModule } from 'primeng/toast';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CardModule,
    PanelModule,
    TagModule,
    ChipModule,
    ButtonModule,
    ProgressSpinnerModule,
    DividerModule,
    AvatarModule,
    TableModule,
    BadgeModule,
    AccordionModule,
    SkeletonModule,
    ToastModule,
    AppDatePipe
  ],
  providers: [MessageService],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})
export class UserProfileComponent implements OnInit, OnDestroy {

  user: AppUser | null = null;
  isLoading = true;
  isUploading = false;
  private readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  private subscriptions: Subscription = new Subscription();

  // Resolved names for access levels
  userBranches: BranchSummary[] = [];
  userBusinessUnits: BusinessUnitSummary[] = [];
  userBusinesses: BusinessSummary[] = [];
  userBrands: BrandSummary[] = [];
  userSuppliers: SupplierSummary[] = [];
  userCustomers: CustomerSummary[] = [];

  // A restructured array for the accordion
  accessLevels: { title: string, items: any[] }[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private branchesService: BranchesService,
    private brandsService: BrandsService,
    private businessUnitsService: BusinessUnitsService,
    private businessService: BusinessService,
    private suppliersService: SuppliersService,
    private customersService: CustomersService,
    private storageService: FirebaseStorageService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const paramSub = this.route.paramMap.pipe(
      switchMap(params => {
        const userId = params.get('id');
        if (!userId) {
          this.isLoading = false;
          // Optionally, navigate away or show an error
          return of(null);
        }
        this.isLoading = true;
        return this.userService.getUserById(+userId);
      }),
      switchMap(user => {
        if (!user) {
          this.isLoading = false;
          return of(null);
        }
        this.user = user;
        return this.loadRelatedData(user);
      })
    ).subscribe({
      next: (relatedData) => {
        if (relatedData) {
          this.userBranches = relatedData.branches;
          this.userBusinessUnits = relatedData.businessUnits;
          this.userBusinesses = relatedData.businesses;
          this.userBrands = relatedData.brands;
          this.userSuppliers = relatedData.suppliers;
          this.userCustomers = relatedData.customers;
        }
        this.buildAccessLevels();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading user profile data:', err);
        this.isLoading = false;
        // Handle error (e.g., show a toast message)
      }
    });

    this.subscriptions.add(paramSub);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    if (!this.user) {
      return;
    }

    if (file.size > this.maxFileSize) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File size exceeds 100MB limit.' });
        return;
    }
    if (!file.type.startsWith('image/')) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select an image file.' });
        return;
    }

    this.isUploading = true;
    const filePath = `profile-pictures/${this.user.id}/${file.name}`;

    this.storageService.uploadFile(file, filePath).subscribe({
      next: (downloadURL) => {
        if (this.user) {
          this.user.profilePictureUrl = downloadURL;
          this.userService.updateUser(this.user).subscribe({
            next: (updatedUser) => {
              this.user = updatedUser;
              this.isUploading = false;
              this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Profile picture updated!' });
            },
            error: (err) => {
              this.isUploading = false;
              console.error('Error updating user after upload:', err);
              this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not save the new profile picture.' });
            }
          });
        }
      },
      error: (err) => {
        this.isUploading = false;
        console.error('Error uploading file:', err);
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'There was an error uploading your picture.' });
      }
    });
  }

  private loadRelatedData(user: AppUser) {
    const noPaging = { page: 0, size: 5000 };
    return forkJoin({
      branches: this.branchesService.getAllBranches(noPaging).pipe(map(page => this.filterByIds(page.content, user.branchIds, 'id') as BranchSummary[])),
      businessUnits: this.businessUnitsService.getAllBusinessUnits(noPaging).pipe(map(page => this.filterByIds(page.content, user.businessUnitIds, 'id', 'businessUnitName') as BusinessUnitSummary[])),
      businesses: this.businessService.getAllBusinesss(noPaging).pipe(map(page => this.filterByIds(page.content, user.businessIds, 'id', 'businessName') as BusinessSummary[])),
      brands: this.brandsService.getAllBrands(noPaging).pipe(map(page => this.filterByIds(page.content, user.brandIds, 'id', 'brandName') as BrandSummary[])),
      suppliers: this.suppliersService.getAllSuppliers(noPaging).pipe(map(page => this.filterByIds(page.content, user.supplierIds, 'id', 'supplierName') as SupplierSummary[])),
      customers: this.customersService.getAllCustomers(noPaging).pipe(map(page => this.filterByIds(page.content, user.customerIds, 'id', 'customerName') as CustomerSummary[]))
    });
  }

  private filterByIds(items: any[], ids: number[] | undefined, idField: string, nameField: string = 'name'): any[] {
    if (!ids || ids.length === 0 || !items || items.length === 0) {
      return [];
    }
    const idSet = new Set(ids);
    return items
      .filter(item => idSet.has(item[idField]))
      .map(item => ({ id: item[idField], name: item[nameField] }));
  }

  getStatusColor(status?: string): string {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'pending_verification': return 'info';
      case 'deactivated': return 'danger';
      default: return 'secondary';
    }
  }

  goBackToList(): void {
    this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['users', 'list'] } }]);
  }

  editUser(): void {
    if (this.user) {
      this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['users', 'edit', this.user.id] } }]);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }


  // --- HELPER FUNCTIONS FOR THE TEMPLATE ---

  /**
   * Generates initials from a full name.
   * e.g., "John Doe" -> "JD"
   */
  getInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  /**
   * Creates a semi-random, consistent background color for the avatar
   * based on the user's name.
   */
  getAvatarColor(name: string): string {
    if (!name) return '#000000';
    const colors = ['#FFC107', '#F44336', '#9C27B0', '#03A9F4', '#4CAF50', '#E91E63'];
    const charCodeSum = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[charCodeSum % colors.length];
  }
  
  /**
   * Maps user status to a PrimeNG severity for component styling.
   */
  getStatusSeverity(status: string): 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'warn';
      case 'inactive':
        return 'danger';
      default:
        return 'info';
    }
  }

  /**
   * Restructures user access data for the accordion component.
   */
  private buildAccessLevels(): void {
      if (!this.user) return;
      this.accessLevels = [
        { title: 'Branches', items: this.userBranches },
        { title: 'Business Units', items: this.userBusinessUnits },
        { title: 'Businesses', items: this.userBusinesses },
        { title: 'Brands', items: this.userBrands },
        { title: 'Suppliers', items: this.userSuppliers },
        { title: 'Customers', items: this.userCustomers },
      ].filter(level => level.items && level.items.length > 0); // Optionally filter out empty sections
  }
}
